import React from "react";
import logger from "@utils/logger";
import { logoutUser } from "@services/auth";

/**
 * Last-resort catch for render errors. Without it, a crash anywhere in the
 * tree leaves a permanent white screen — and because every route redirects
 * to "/" while an access token exists, the user can never reach the login
 * page again until they manually clear site data.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logger.error(
      `Unhandled render error: ${error?.message || error}\n${
        errorInfo?.componentStack || ""
      }`
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  handleLogout = () => {
    // Clears all session storage keys and redirects to "/", which renders
    // the login page once no token is present.
    logoutUser();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50">
            <svg
              className="h-7 w-7 text-rose-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-slate-800 mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            The page hit an unexpected error. You can reload and try again, or
            sign out and log back in with a fresh session.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleLogout}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Log out and sign in again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
